/* ============================================================
   Fence Works — the coating visualizer's catalog and geometry
   (docs/SERVICE_SWITCHER_PLAN.md §13 item 14). Pure, import-free: no DOM,
   so the unit harness transpiles it alone; coatpreview.tsx does the drawing.

   • FLAKE_BLENDS — the vinyl-flake blends a garage floor is sold in, each a
     base colour and the four chips that make the speckle
   • FINISHES — how shiny the topcoat reads
   • specks() — a deterministic speckle field (same seed, same floor), sized
     for a texture tile, the broadcast density by system (full / partial)
   • homography() / applyH() — the unit square onto the four corners the owner
     taps on the photo, so the tile lies flat on the floor in perspective
   • orderCorners() — whatever order the corners were tapped in
   ============================================================ */
export type FlakeBlend = { id: string; name: string; base: string; flakes: string[]; family: "neutral" | "warm" | "cool" | "bold" };
export type Finish = { id: string; name: string; sheen: number };
export type Pt = [number, number];

export const FLAKE_BLENDS: FlakeBlend[] = [
  { id: "granite", name: "Granite", base: "#8d8f93", flakes: ["#2b2d31", "#6b6e73", "#b8bbc0", "#e8e9eb"], family: "neutral" },
  { id: "domino", name: "Domino", base: "#7a7a7a", flakes: ["#111111", "#3d3d3d", "#f4f4f4", "#bdbdbd"], family: "neutral" },
  { id: "tidal-wave", name: "Tidal Wave", base: "#6f7f8a", flakes: ["#1f3a4d", "#4f7d94", "#a9bcc6", "#ecf0f2"], family: "cool" },
  { id: "nightfall", name: "Nightfall", base: "#3a3d44", flakes: ["#1a1c22", "#5a5f6b", "#8a909c", "#2f3f5c"], family: "cool" },
  { id: "sandstone", name: "Sandstone", base: "#b79a74", flakes: ["#7a5a3a", "#d8c3a2", "#f0e6d6", "#9a7b5a"], family: "warm" },
  { id: "cabin-fever", name: "Cabin Fever", base: "#8a6a4e", flakes: ["#3c2a1e", "#b48a62", "#e2cbb0", "#5e4634"], family: "warm" },
  { id: "tuxedo", name: "Tuxedo", base: "#5c5f66", flakes: ["#0d0f12", "#ffffff", "#8e939c", "#2a2d33"], family: "bold" },
  { id: "comet", name: "Comet", base: "#7d8a8f", flakes: ["#2d6a7a", "#c7a24a", "#e9eef0", "#405a63"], family: "bold" },
];
export const FINISHES: Finish[] = [
  { id: "gloss", name: "High gloss", sheen: 0.45 },
  { id: "satin", name: "Satin", sheen: 0.22 },
  { id: "matte", name: "Matte", sheen: 0.08 },
];
export const DEFAULT_BLEND = "granite";
export const DEFAULT_FINISH = "gloss";

export function blendOf(id: any): FlakeBlend { return FLAKE_BLENDS.find((b) => b.id === id) || FLAKE_BLENDS[0]; }
export function finishOf(id: any): Finish { return FINISHES.find((f) => f.id === id) || FINISHES[0]; }
/* "Tidal Wave · full flake · satin" — the label on the saved preview and the quote line */
export function visualLabel(blend: any, finish: any, system: any): string {
  const sys = system === "partial-flake" ? "partial flake" : system === "solid" ? "solid colour" : "full flake";
  return [blendOf(blend).name, sys, finishOf(finish).name.toLowerCase()].join(" · ");
}

/* a small deterministic PRNG (mulberry32): the same seed draws the same floor */
export function mulberry32(seed: number): () => number {
  let a = (seed >>> 0) || 1;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Speck = { x: number; y: number; r: number; rot: number; color: string; k: number };
/* the speckle field for a w × h texture tile. A full broadcast covers the
   floor; a partial one scatters; a solid colour has none. `density` scales
   the count (0.5–1.5 from the slider). Sizes are for a ~1024 px tile. */
export function specks(seed: number, w: number, h: number, blend: FlakeBlend, opts: { density?: number; system?: string } = {}): Speck[] {
  const sys = opts.system || "full-flake";
  if (sys === "solid" || !w || !h) return [];
  const density = Math.max(0.25, Math.min(2, Number(opts.density) || 1));
  const per = sys === "partial-flake" ? 260 : 62;   // px² per flake
  const n = Math.round((w * h) / per * density);
  const rnd = mulberry32(seed);
  const out: Speck[] = [];
  const colors = blend.flakes.length ? blend.flakes : [blend.base];
  for (let i = 0; i < n; i++) {
    const k = Math.floor(rnd() * colors.length);
    out.push({ x: rnd() * w, y: rnd() * h, r: 1.4 + rnd() * 3.2, rot: rnd() * Math.PI, color: colors[k], k });
  }
  return out;
}

/* ---- the unit square onto the floor's four corners --------------------- */
function solve(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-12) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}
/* the 3×3 (row-major, 9 numbers) that takes src[i] to dst[i]; null when the points are degenerate */
export function homography(src: Pt[], dst: Pt[]): number[] | null {
  if (!Array.isArray(src) || !Array.isArray(dst) || src.length !== 4 || dst.length !== 4) return null;
  const A: number[][] = [], b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i], [X, Y] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -X * x, -X * y]); b.push(X);
    A.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]); b.push(Y);
  }
  const h = solve(A, b);
  return h ? [...h, 1] : null;
}
export function applyH(H: number[], x: number, y: number): Pt {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / (w || 1e-9), (H[3] * x + H[4] * y + H[5]) / (w || 1e-9)];
}
/* the four taps in drawing order: top-left, top-right, bottom-right, bottom-left (by angle around the centroid) */
export function orderCorners(pts: Pt[]): Pt[] {
  if (!Array.isArray(pts) || pts.length !== 4) return pts;
  const cx = pts.reduce((s, p) => s + p[0], 0) / 4, cy = pts.reduce((s, p) => s + p[1], 0) / 4;
  const sorted = pts.slice().sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));   // clockwise on screen (y down)
  // start at the top-left: the smallest x + y
  let start = 0;
  for (let i = 1; i < 4; i++) if (sorted[i][0] + sorted[i][1] < sorted[start][0] + sorted[start][1]) start = i;
  return [0, 1, 2, 3].map((i) => sorted[(start + i) % 4]);
}
/* a starting quad for a photo of w × h: the lower two thirds, a little perspective */
export function defaultQuad(w: number, h: number): Pt[] {
  return [[w * 0.18, h * 0.46], [w * 0.82, h * 0.46], [w * 0.98, h * 0.96], [w * 0.02, h * 0.96]];
}
/* the grid cells (unit square) the texture is drawn in, n per side */
export function unitCells(n: number): { u0: number; v0: number; u1: number; v1: number }[] {
  const k = Math.max(1, Math.min(64, Math.round(n) || 1));
  const out: { u0: number; v0: number; u1: number; v1: number }[] = [];
  for (let j = 0; j < k; j++) for (let i = 0; i < k; i++) out.push({ u0: i / k, v0: j / k, u1: (i + 1) / k, v1: (j + 1) / k });
  return out;
}
/* the affine transform (a, b, c, d, e, f) taking triangle s0,s1,s2 onto d0,d1,d2 */
export function triangleAffine(s: [Pt, Pt, Pt], d: [Pt, Pt, Pt]): number[] | null {
  const [[x0, y0], [x1, y1], [x2, y2]] = s;
  const det = (x1 - x0) * (y2 - y0) - (x2 - x0) * (y1 - y0);
  if (Math.abs(det) < 1e-12) return null;
  const [[u0, v0], [u1, v1], [u2, v2]] = d;
  const a = ((u1 - u0) * (y2 - y0) - (u2 - u0) * (y1 - y0)) / det;
  const b = ((v1 - v0) * (y2 - y0) - (v2 - v0) * (y1 - y0)) / det;
  const c = ((u2 - u0) * (x1 - x0) - (u1 - u0) * (x2 - x0)) / det;
  const dd = ((v2 - v0) * (x1 - x0) - (v1 - v0) * (x2 - x0)) / det;
  return [a, b, c, dd, u0 - a * x0 - c * y0, v0 - b * x0 - dd * y0];
}
